/* Maison Empereur — <me-header> custom element
   Encapsulates banner rotation/collapse, scroll behavior, and drawer
   open/close + drilldown. Loaded as ES module from sections/me-header.liquid. */

import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const SCROLL_THRESHOLD = 80;
const SCROLL_THRESHOLD_EXIT = 40;
const SCROLL_DELTA_MIN = 6;
const SCROLL_STATE_COOLDOWN_MS = 400;
const RESIZE_DEBOUNCE_MS = 120;
// Transparent header turns solid as soon as the page leaves the very top,
// regardless of scroll direction (transparency is top-only).
const STUCK_THRESHOLD = 8;

class MeHeader extends HTMLElement {
  constructor() {
    super();
    this._bannerTimer = null;
    this._lastScrollY = 0;
    this._scrollRAF = null;
    this._panelStack = ['root'];
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    this._scrollTarget = null;
    this._onDrawerClick = this._onDrawerClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onCartUpdate = this._onCartUpdate.bind(this);
    this._resizeTimer = null;
    this._drawerDefaultTitle = 'Menu';
  }

  connectedCallback() {
    this._initBanner();
    this._initScroll();
    this._initDrawer();
    this._recomputeHeaderHeights();

    // Valeurs rendues par le serveur (vraies pour la page courante). Servent à
    // les restaurer après un morph cart-update (cf _onCartUpdate) — le morph
    // re-render dans un contexte 'index', qui fausse aussi data-scroll-behavior
    // (le CSS sticky/fixed est keyé dessus).
    this._initialTransparent = this.hasAttribute('data-transparent');
    this._initialScrollBehavior = this.dataset.scrollBehavior;

    // 4.0 : le scroll provient de .page-wrapper en desktop (≥990px) et de
    // document.scrollingElement en mobile (cf @theme/scroll-container). Écouter
    // window ne déclenchait plus rien en desktop → header figé dans son état initial.
    this._scrollTarget = getScrollEventTarget();
    this._scrollTarget.addEventListener('scroll', this._onScroll, { passive: true });
    scrollContainerMediaQuery.addEventListener('change', this._onBreakpointChange);
    window.addEventListener('resize', this._onResize, { passive: true });
    document.addEventListener('keydown', this._onKeydown);
    // 4.0 : l'event panier est shopify:cart:lines-update (ex-'cart:update' en 3.5.1).
    document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);
  }

  // Sur cart-update : (1) met à jour le badge compteur du trigger — il est hors de
  // la zone morphée par cart-items-component, donc il ne se rafraîchit pas seul ;
  // (2) restaure l'état header (transparent/stuck) après un éventuel morph de section.
  _onCartUpdate(event) {
    event?.promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        const badge = this.querySelector('[data-cart-count]');
        if (badge) {
          badge.textContent = count;
          badge.toggleAttribute('hidden', count === 0);
        }
      })
      .catch(() => {});

    requestAnimationFrame(() => {
      this.toggleAttribute('data-transparent', this._initialTransparent);
      if (this._initialScrollBehavior) {
        this.setAttribute('data-scroll-behavior', this._initialScrollBehavior);
      }
      let y = getScrollTop();
      if (document.body.style.position === 'fixed') {
        y = Math.abs(parseInt(document.body.style.top || '0', 10)) || y;
      }
      this.toggleAttribute('data-stuck', y > STUCK_THRESHOLD);
      this.toggleAttribute('data-scrolled', y > SCROLL_THRESHOLD);
      this.toggleAttribute('data-banner-collapsed', y > SCROLL_THRESHOLD);
    });
  }

  disconnectedCallback() {
    if (this._bannerTimer) clearInterval(this._bannerTimer);
    if (this._scrollRAF) cancelAnimationFrame(this._scrollRAF);
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    if (this._recomputeTimer) clearTimeout(this._recomputeTimer);
    this._scrollTarget?.removeEventListener('scroll', this._onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this._onBreakpointChange);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
    this._unlockBodyScroll();
  }

  // ---------- Banner ----------

  _initBanner() {
    const messages = this.querySelectorAll('[data-banner-message]');
    if (messages.length <= 1) return;

    const seconds = parseFloat(this.dataset.bannerRotationSeconds) || 5;
    let current = 0;

    this._bannerTimer = setInterval(() => {
      messages[current].removeAttribute('data-active');
      current = (current + 1) % messages.length;
      messages[current].setAttribute('data-active', 'true');
    }, seconds * 1000);
  }

  // ---------- Scroll ----------

  _initScroll() {
    this._lastScrollY = getScrollTop();
    this._applyScrollState();
  }

  _onScroll() {
    if (this._scrollRAF) return;
    this._scrollRAF = requestAnimationFrame(() => {
      this._scrollRAF = null;
      this._applyScrollState();
    });
  }

  // Le conteneur de scroll bascule .page-wrapper (desktop ≥990px) ↔ scrollingElement
  // (mobile) au passage du breakpoint. On rebind le listener sur le nouveau scroller,
  // sinon les events viennent d'une cible périmée après un resize (cf header.js natif 4.0).
  _onBreakpointChange() {
    this._scrollTarget?.removeEventListener('scroll', this._onScroll);
    this._scrollTarget = getScrollEventTarget();
    this._scrollTarget.addEventListener('scroll', this._onScroll, { passive: true });
    this._lastScrollY = getScrollTop();
    this._applyScrollState();
  }

  _applyScrollState() {
    // Pendant un scroll-lock de dialog (cart drawer, modale, galerie zoom),
    // dialog.js met body{position:fixed; top:-scrollY} → window.scrollY lit 0.
    // Si le header est re-rendu à cet instant (maj du badge panier sur cart-update),
    // son connectedCallback rappelle _applyScrollState : il faut récupérer la vraie
    // position depuis body.top, sinon data-stuck n'est pas posé et le header repasse
    // transparent derrière le drawer ouvert.
    let y = getScrollTop();
    if (document.body.style.position === 'fixed') {
      const locked = Math.abs(parseInt(document.body.style.top || '0', 10));
      if (locked) y = locked;
    }
    const delta = y - this._lastScrollY;
    const now = performance.now();

    // Compact mode: enter past SCROLL_THRESHOLD while scrolling DOWN,
    // restore to full mode whenever the user scrolls UP (so the search
    // pill / banner come back even if still past the threshold), or once
    // back near the top. Hysteresis on the entry side prevents flicker.
    // Cooldown after each toggle absorbs the scrollY rebound caused by
    // the header height transition reflow.
    const wasScrolled = this.hasAttribute('data-scrolled');
    let scrolled = wasScrolled;
    const inCooldown = this._scrolledCooldownUntil && now < this._scrolledCooldownUntil;

    if (!inCooldown) {
      const scrollingDown = delta > SCROLL_DELTA_MIN;
      const scrollingUp = delta < -SCROLL_DELTA_MIN;

      if (!wasScrolled && scrollingDown && y > SCROLL_THRESHOLD) {
        scrolled = true;
      } else if (wasScrolled && (scrollingUp || y < SCROLL_THRESHOLD_EXIT)) {
        scrolled = false;
      }
    }

    this.toggleAttribute('data-scrolled', scrolled);
    if (wasScrolled !== scrolled) {
      this._scrolledCooldownUntil = now + SCROLL_STATE_COOLDOWN_MS;
      this._scheduleRecompute();
    }

    // Banner: collapses past threshold and STAYS collapsed (even on scroll up)
    // until the user returns to the very top of the page. Decoupled from
    // data-scrolled so the compact-mode reveal (search pill, nav) doesn't
    // bring the banner back mid-page.
    if (this.dataset.hideBannerOnScroll === 'true') {
      const wasBannerCollapsed = this.hasAttribute('data-banner-collapsed');
      let bannerCollapsed = wasBannerCollapsed;
      if (!wasBannerCollapsed && y > SCROLL_THRESHOLD) {
        bannerCollapsed = true;
      } else if (wasBannerCollapsed && y < 10) {
        bannerCollapsed = false;
      }
      this.toggleAttribute('data-banner-collapsed', bannerCollapsed);
      if (wasBannerCollapsed !== bannerCollapsed) {
        this._scheduleRecompute();
      }
    }

    // Transparent header: only transparent at the very top of the page. As
    // soon as the page is scrolled at all, mark it stuck so it turns solid
    // (independent of data-scrolled, which reveals on scroll-up mid-page).
    this.toggleAttribute('data-stuck', y > STUCK_THRESHOLD);

    // Per-template scroll behavior
    const behavior = this.dataset.scrollBehavior;
    if (behavior === 'scroll-up') {
      if (Math.abs(delta) >= SCROLL_DELTA_MIN) {
        if (delta > 0 && y > SCROLL_THRESHOLD) {
          this.setAttribute('data-hidden', '');
        } else if (delta < 0) {
          this.removeAttribute('data-hidden');
        }
      }
    } else {
      this.removeAttribute('data-hidden');
    }

    this._lastScrollY = y;
  }

  _onResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => this._recomputeHeaderHeights(), RESIZE_DEBOUNCE_MS);
  }

  _scheduleRecompute() {
    // Wait for height transitions to settle before measuring
    if (this._recomputeTimer) clearTimeout(this._recomputeTimer);
    this._recomputeTimer = setTimeout(() => this._recomputeHeaderHeights(), 320);
  }

  _recomputeHeaderHeights() {
    const headerGroup = document.getElementById('header-group');
    const headerHeight = this.offsetHeight;
    let groupHeight = 0;
    if (headerGroup) {
      for (const el of headerGroup.children) {
        if (!(el instanceof HTMLElement)) continue;
        // .me-header-section est en display:contents (cf me-header.liquid :
        // positionnement sans :has()) → pas de boîte, offsetHeight = 0 :
        // on mesure alors ses enfants (me-header) à la place.
        let h = el.offsetHeight;
        if (h === 0) {
          for (const child of el.children) {
            if (child instanceof HTMLElement) h += child.offsetHeight;
          }
        }
        groupHeight += h;
      }
    } else {
      groupHeight = headerHeight;
    }
    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
  }

  // ---------- Drawer ----------

  _initDrawer() {
    this._drawer = document.querySelector('[data-me-drawer]');
    this._overlay = document.querySelector('[data-me-drawer-overlay]');
    this._drawerTitle = this._drawer?.querySelector('[data-me-drawer-title]');
    // Titre par défaut = celui rendu par Liquid (traduit), pas un littéral FR/EN.
    this._drawerDefaultTitle = this._drawerTitle?.textContent || 'Menu';
    this._drawerBack = this._drawer?.querySelector('[data-me-drawer-back]');
    this._panels = this._drawer?.querySelectorAll('[data-panel-id]');

    if (!this._drawer || !this._overlay) return;

    // Open trigger (burger in header)
    this.addEventListener('click', (e) => {
      const trigger = e.target instanceof Element
        ? e.target.closest('[data-me-drawer-trigger]')
        : null;
      if (trigger) {
        e.preventDefault();
        this._openDrawer(trigger);
      }
    });

    // Close + overlay + back + drilldown handled inside the drawer
    this._drawer.addEventListener('click', this._onDrawerClick);
    this._overlay.addEventListener('click', () => this._closeDrawer());
  }

  _onDrawerClick(e) {
    if (e.target.closest('[data-me-drawer-close]')) {
      e.preventDefault();
      this._closeDrawer();
      return;
    }
    if (e.target.closest('[data-me-drawer-back]')) {
      e.preventDefault();
      this._popPanel();
      return;
    }
    const target = e.target.closest('[data-panel-target]');
    if (target) {
      e.preventDefault();
      this._pushPanel(target.getAttribute('data-panel-target'));
    }
  }

  _openDrawer(trigger) {
    this._lastDrawerTrigger = trigger || document.activeElement;
    this._drawer.setAttribute('data-open', '');
    this._drawer.setAttribute('aria-hidden', 'false');
    this._drawer.removeAttribute('inert');
    this._overlay.setAttribute('data-open', '');
    this._overlay.setAttribute('aria-hidden', 'false');
    this._lockBodyScroll();
    this._resetPanels();

    // Inert siblings (main, footer) so focus + AT can't leak out
    this._inertSiblings = [];
    document.querySelectorAll('main, footer').forEach((el) => {
      if (!el.hasAttribute('inert')) {
        el.setAttribute('inert', '');
        this._inertSiblings.push(el);
      }
    });

    // Move focus to first focusable inside the drawer
    requestAnimationFrame(() => {
      const first = this._drawer.querySelector(this._focusableSelector());
      first?.focus();
    });
  }

  _closeDrawer() {
    this._drawer.removeAttribute('data-open');
    this._drawer.setAttribute('aria-hidden', 'true');
    this._drawer.setAttribute('inert', '');
    this._overlay.removeAttribute('data-open');
    this._overlay.setAttribute('aria-hidden', 'true');
    this._unlockBodyScroll();

    this._inertSiblings?.forEach((el) => el.removeAttribute('inert'));
    this._inertSiblings = [];

    // Restore focus to the element that opened the drawer
    this._lastDrawerTrigger?.focus?.();
    this._lastDrawerTrigger = null;
  }

  _focusableSelector() {
    return [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
  }

  _trapFocus(e) {
    const focusables = Array.from(
      this._drawer.querySelectorAll(this._focusableSelector())
    ).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);

    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && (active === first || !this._drawer.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _pushPanel(panelId) {
    const target = this._drawer.querySelector(`[data-panel-id="${CSS.escape(panelId)}"]`);
    if (!target) return;
    const current = this._panelStack[this._panelStack.length - 1];
    const currentEl = this._drawer.querySelector(`[data-panel-id="${CSS.escape(current)}"]`);
    if (currentEl) {
      currentEl.classList.remove('is-active');
      currentEl.classList.add('is-past');
    }
    target.classList.add('is-active');
    target.classList.remove('is-past');
    this._panelStack.push(panelId);
    this._updateDrawerHead();
  }

  _popPanel() {
    if (this._panelStack.length <= 1) return;
    const currentId = this._panelStack.pop();
    const currentEl = this._drawer.querySelector(`[data-panel-id="${CSS.escape(currentId)}"]`);
    const prevId = this._panelStack[this._panelStack.length - 1];
    const prevEl = this._drawer.querySelector(`[data-panel-id="${CSS.escape(prevId)}"]`);
    if (currentEl) currentEl.classList.remove('is-active', 'is-past');
    if (prevEl) {
      prevEl.classList.remove('is-past');
      prevEl.classList.add('is-active');
    }
    this._updateDrawerHead();
  }

  _resetPanels() {
    this._panelStack = ['root'];
    if (!this._panels) return;
    for (const p of this._panels) {
      p.classList.remove('is-past');
      p.classList.toggle('is-active', p.getAttribute('data-panel-id') === 'root');
    }
    this._updateDrawerHead();
  }

  _updateDrawerHead() {
    const currentId = this._panelStack[this._panelStack.length - 1];
    const currentEl = this._drawer.querySelector(`[data-panel-id="${CSS.escape(currentId)}"]`);
    if (this._drawerTitle && currentEl) {
      this._drawerTitle.textContent = currentEl.getAttribute('data-panel-title') || this._drawerDefaultTitle;
    }
    if (this._drawerBack) {
      this._drawerBack.toggleAttribute('hidden', this._panelStack.length <= 1);
    }
  }

  _onKeydown(e) {
    if (!this._drawer?.hasAttribute('data-open')) return;
    if (e.key === 'Escape') {
      this._closeDrawer();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  // ---------- Body scroll lock ----------

  _lockBodyScroll() {
    this._scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  _unlockBodyScroll() {
    if (this._scrollY == null) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY);
    this._scrollY = null;
  }
}

if (!customElements.get('me-header')) {
  customElements.define('me-header', MeHeader);
}
